import { Link } from 'react-router-dom';
import { UserStatus, type Badge as EarnedBadge, type TeenOverview } from '@teentack/shared';
import { useAuth } from '@/auth/use-auth';
import { useBadges } from '@/features/badges/use-badges';
import { useMyAssignments } from '@/features/assignments/use-assignments';
import { useUnreadCount } from '@/features/notifications/use-notifications';
import { useUnreadMessageCount } from '@/features/messaging/use-messaging';
import { useReviews } from '@/features/reviews/use-reviews';
import { useLocation } from '@/features/worksite/use-worksite';
import { Badge, type BadgeTone } from '@/components/ui/Badge';

/**
 * End-to-end status tracking. [Teen Phase 6]
 *
 * The reference journey puts every stage of the teen's experience on one board
 * — registration through to badges — so they can answer "where am I?" without
 * opening five screens. Each tile is a link to the place that stage lives,
 * because a status nobody can act on is just decoration.
 */

interface Tile {
  label: string;
  value: string;
  tone: BadgeTone;
  to: string;
  hint?: string;
}

function StatusTile({ tile }: { tile: Tile }) {
  return (
    <Link
      to={tile.to}
      className="flex flex-col gap-2 rounded-xl border border-line bg-white p-4 transition hover:border-brand-300 hover:shadow-soft"
    >
      <span className="text-[0.7rem] font-bold uppercase tracking-wide text-slate-400">
        {tile.label}
      </span>
      <Badge tone={tile.tone}>{tile.value}</Badge>
      {tile.hint && <span className="text-xs text-slate-500">{tile.hint}</span>}
    </Link>
  );
}

function BadgeCard({ badge }: { badge: EarnedBadge }) {
  const pct = badge.progress === null ? null : Math.round(badge.progress * 100);

  return (
    <li
      className={`rounded-xl border p-4 ${
        badge.earned ? 'border-sun-300 bg-sun-50' : 'border-line bg-white'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <p
          className={`text-sm font-bold ${badge.earned ? 'text-sun-900' : 'text-slate-500'}`}
        >
          {badge.name}
        </p>
        <span aria-hidden="true" className={badge.earned ? 'text-sun-500' : 'text-slate-300'}>
          {badge.earned ? '★' : '☆'}
        </span>
      </div>

      <p className={`mt-1 text-xs leading-relaxed ${badge.earned ? 'text-sun-800' : 'text-slate-500'}`}>
        {badge.description}
      </p>

      {/* Only locked badges get a bar — a finished one does not need a meter. */}
      {!badge.earned && pct !== null && (
        <div className="mt-2">
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
            <div className="h-full rounded-full bg-brand-500" style={{ width: `${pct}%` }} />
          </div>
          {badge.progressLabel && (
            <p className="mt-1 text-[0.7rem] font-medium text-slate-400">
              {badge.progressLabel}
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export function TeenStatusBoard({ overview }: { overview: TeenOverview }) {
  const { user } = useAuth();

  const badges = useBadges();
  const assignments = useMyAssignments({ pageSize: 20 });
  const unreadAlerts = useUnreadCount();
  const unreadMessages = useUnreadMessageCount();
  const reviews = useReviews({ pageSize: 50 });

  const active = (assignments.data?.items ?? []).find((assignment) =>
    ['ACCEPTED', 'IN_PROGRESS'].includes(assignment.status),
  );
  // Only the one job actually under way is worth polling for a position.
  const location = useLocation(active?.id, Boolean(active));

  const reviewItems = reviews.data?.items ?? [];
  const average =
    reviewItems.length > 0
      ? Math.round(
          (reviewItems.reduce((sum, review) => sum + review.rating, 0) / reviewItems.length) * 10,
        ) / 10
      : null;

  const profileSteps = [
    overview.skillCount > 0,
    overview.hasBio,
    overview.hasAvailability,
  ].filter(Boolean).length;

  const earned = badges.data?.earnedCount ?? 0;
  const totalBadges = badges.data?.badges.length ?? 0;

  /** Every stage of the journey, in the order the reference lays it out. */
  const tiles: Tile[] = [
    {
      label: 'Registration',
      value: user?.status === UserStatus.ACTIVE ? 'Verified' : 'Pending',
      tone: user?.status === UserStatus.ACTIVE ? 'safe' : 'sun',
      to: '/profile',
    },
    {
      label: 'Parent approval',
      value: !overview.isMinor
        ? 'Not needed'
        : overview.guardianApproved
          ? 'Approved'
          : 'Waiting',
      tone: !overview.isMinor ? 'neutral' : overview.guardianApproved ? 'safe' : 'sun',
      to: '/profile',
      ...(overview.guardianName ? { hint: overview.guardianName } : {}),
    },
    {
      label: 'Saved jobs',
      value: String(overview.savedJobs),
      tone: overview.savedJobs > 0 ? 'brand' : 'neutral',
      to: '/saved',
    },
    {
      label: 'Parent consent',
      value:
        overview.awaitingGuardian > 0
          ? `${overview.awaitingGuardian} waiting`
          : overview.readyToApply > 0
            ? `${overview.readyToApply} approved`
            : 'Nothing pending',
      tone: overview.awaitingGuardian > 0 ? 'sun' : overview.readyToApply > 0 ? 'safe' : 'neutral',
      to: '/my-work',
    },
    {
      label: 'Applications',
      value: String(overview.openApplications + overview.shortlistedApplications),
      tone: overview.shortlistedApplications > 0 ? 'accent' : 'neutral',
      to: '/my-work',
      ...(overview.shortlistedApplications > 0
        ? { hint: `${overview.shortlistedApplications} shortlisted` }
        : {}),
    },
    {
      label: 'Job offers',
      value: overview.jobOffers > 0 ? `${overview.jobOffers} to answer` : 'None',
      tone: overview.jobOffers > 0 ? 'sun' : 'neutral',
      to: '/my-work',
    },
    {
      label: 'Live job',
      value: active ? active.status.charAt(0) + active.status.slice(1).toLowerCase().replace('_', ' ') : 'None right now',
      tone: active ? 'brand' : 'neutral',
      to: '/my-work',
      ...(active ? { hint: active.job.title } : {}),
    },
    {
      label: 'Location sharing',
      value: location.data?.sharing ? 'On' : 'Off',
      tone: location.data?.sharing ? 'safe' : 'neutral',
      to: '/my-work',
      hint: location.data?.sharing ? 'Guardian and employer can see it' : 'Your choice, always',
    },
    {
      label: 'Messages',
      value:
        (unreadMessages.data?.unread ?? 0) > 0
          ? `${unreadMessages.data?.unread} unread`
          : 'All read',
      tone: (unreadMessages.data?.unread ?? 0) > 0 ? 'brand' : 'neutral',
      to: '/messages',
    },
    {
      label: 'Notifications',
      value:
        (unreadAlerts.data?.unread ?? 0) > 0 ? `${unreadAlerts.data?.unread} new` : 'Up to date',
      tone: (unreadAlerts.data?.unread ?? 0) > 0 ? 'brand' : 'neutral',
      to: '/notifications',
    },
    {
      label: 'Work history',
      value: `${overview.completedJobs} finished`,
      tone: overview.completedJobs > 0 ? 'safe' : 'neutral',
      to: '/my-work',
    },
    {
      label: 'Reviews',
      value: average === null ? 'Not rated yet' : `${average.toFixed(1)} average`,
      tone: average === null ? 'neutral' : 'accent',
      to: '/profile',
      ...(reviewItems.length > 0 ? { hint: `${reviewItems.length} reviews` } : {}),
    },
    {
      label: 'Profile',
      value: `${profileSteps} of 3 done`,
      tone: profileSteps === 3 ? 'safe' : 'sun',
      to: '/profile',
    },
    {
      label: 'Badges',
      value: `${earned} of ${totalBadges}`,
      tone: earned > 0 ? 'accent' : 'neutral',
      to: '/teen',
    },
  ];

  return (
    <section>
      <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-bold text-ink">Everything at a glance</h2>
        <span className="text-xs text-slate-400">Discovery through to earnings</span>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        Every stage of your journey, and where you are on it.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <StatusTile key={tile.label} tile={tile} />
        ))}
      </div>

      {/* Achievement badges — the last tile on the reference dashboard. */}
      {badges.data && badges.data.badges.length > 0 && (
        <div className="mt-8">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-base font-bold text-ink">Badges</h3>
            <span className="text-xs text-slate-400">
              {earned} earned of {totalBadges}
            </span>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {badges.data.badges.map((badge) => (
              <BadgeCard key={badge.slug} badge={badge} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
